// Global
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getTestProps } from 'lib/testing/utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
export const Default = (props)=>{
    const isEditing = useIsEditing();
    const { RenderingIdentifier } = props?.params || {};
    const footerColumns = props.fields?.footerColumns;
    const copyRightText = props?.fields?.copyrightText;
    const sraTitle = props?.fields?.sraTitle;
    const sraInfo = props?.fields?.sraInfo;
    const bottomFooterColumns = props?.fields?.legalLinks;
    // Define keys and values for token replacement
    const footerTokens = [
        {
            key: '{{year}}',
            value: new Date().getFullYear().toString()
        }
    ];
    /*
   * Rendering
   */ if (!props.fields) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    const extendedTailwindVariants = tv({
        extend: TAILWIND_VARIANTS,
        slots: {
            base: [
                props?.params?.styles
            ]
        }
    });
    const { base, contentContainer, linkListContainer, linkListItem, linkListLink, linkListTitle, logoContainer, logoDescription, linkListText, footerSectionWrapper, footerSocialLinksSection, copyRightTextStyle, navContainer, legalLinkWrapper, legalLinkListItem, legalLinkNavContainer, legalLinkListLink, legalLinkListText, linkListItemWrapper } = extendedTailwindVariants();
    return /*#__PURE__*/ _jsxs("div", {
        "data-component": "authorable/shared/site-structure/footer/footer",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-footer-` + props?.rendering?.uid),
        className: base(),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: contentContainer(),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: linkListContainer(),
                        children: footerColumns?.map((groupLabel)=>{
                            const links = groupLabel?.fields?.columnLinks;
                            const columnHeader = groupLabel?.fields?.columnHeader;
                            const columnType = groupLabel?.fields?.columnType?.fields?.Value;
                            const columnTypeValue = columnType?.value?.trim()?.toLowerCase();
                            return /*#__PURE__*/ _jsxs("div", {
                                className: `${linkListItemWrapper()} ${columnTypeValue}`,
                                children: [
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        ...getTestProps(`footer-nav-header`),
                                        className: linkListTitle(),
                                        field: columnHeader,
                                        tag: "h3"
                                    }),
                                    /*#__PURE__*/ _jsx("ul", {
                                        className: navContainer(),
                                        children: links?.map((link)=>{
                                            return /*#__PURE__*/ _jsx("li", {
                                                className: linkListItem(),
                                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                                    ctaComponentClass: "default",
                                                    ...getTestProps(`footer-nav-link`),
                                                    className: linkListLink(),
                                                    field: link?.fields?.link,
                                                    suppressNewTabIcon: true,
                                                    children: /*#__PURE__*/ _jsx("span", {
                                                        className: linkListText(),
                                                        children: link?.fields?.link?.value?.text
                                                    })
                                                })
                                            }, link?.id);
                                        })
                                    })
                                ]
                            }, groupLabel?.id);
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: logoContainer(),
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: linkListItem(),
                                children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    ...getTestProps(`footer-nav-header`),
                                    className: linkListTitle(),
                                    field: sraTitle,
                                    tag: "h3"
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: /*#__PURE__*/ _jsx(RichTextWrapper, {
                                    ...getTestProps(`footer-description`),
                                    className: logoDescription(),
                                    field: sraInfo,
                                    tag: "div"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: footerSectionWrapper(),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: footerSocialLinksSection(),
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: legalLinkWrapper(),
                            children: bottomFooterColumns?.map((groupLabel)=>{
                                const links = groupLabel?.fields?.legalColumnLinks;
                                return /*#__PURE__*/ _jsx("ul", {
                                    className: legalLinkNavContainer(),
                                    children: links?.map((link)=>{
                                        return /*#__PURE__*/ _jsx("li", {
                                            className: legalLinkListItem(),
                                            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                                ctaComponentClass: "default",
                                                ...getTestProps(`footer-nav-link`),
                                                className: legalLinkListLink(),
                                                field: link?.fields?.link,
                                                suppressNewTabIcon: true,
                                                children: /*#__PURE__*/ _jsx("span", {
                                                    className: legalLinkListText(),
                                                    children: link?.fields?.link?.value?.text
                                                })
                                            })
                                        }, link?.id);
                                    })
                                }, groupLabel?.id);
                            })
                        }),
                        /*#__PURE__*/ _jsx(RichTextWrapper, {
                            ...getTestProps('copyright-info'),
                            className: copyRightTextStyle(),
                            field: copyRightText,
                            tokens: footerTokens
                        })
                    ]
                })
            })
        ]
    });
};
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'w-full',
            'bg-component-footer-bg'
        ],
        contentContainer: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-48',
            'lg:flex',
            'lg:flex-row',
            'lg:gap-spacing-spacing-64',
            'lg:pt-spacing-spacing-64',
            'py-spacing-spacing-48',
            'lg:col-span-4',
            'm-auto',
            'max-w-screen-dimensions-max-width',
            'min-w-screen-dimensions-min-width',
            'px-general-spacing-margin-x',
            'py-spacing-spacing-24'
        ],
        linkListContainer: [
            'gap-spacing-spacing-32',
            'grid',
            'grid-cols-2',
            'lg:grid-cols-3',
            'flex-grow'
        ],
        linkListItemWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-16',
            'w-full',
            '[&.contact]:col-start-1',
            '[&.contact]:row-start-1',
            '[&.social]:col-start-1',
            '[&.social]:row-start-2',
            '[&.links]:col-start-2',
            '[&.links]:row-span-2',
            'lg:[&.contact]:col-start-1',
            'lg:[&.contact]:row-start-1',
            'lg:[&.social]:col-start-2',
            'lg:[&.social]:row-start-1',
            'lg:[&.links]:col-start-3',
            'lg:[&.links]:row-span-1'
        ],
        navContainer: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-12',
            'w-full'
        ],
        linkListItem: [],
        linkListLink: [
            'flex',
            'items-center',
            'text-component-footer-link-text',
            'hover:text-component-footer-link-text',
            'focus:text-component-footer-link-text'
        ],
        linkListText: [
            'font-typography-font-weight-body-semibold',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'text-component-footer-link-text',
            'flex',
            'gap-spacing-spacing-8',
            'items-center'
        ],
        linkListTitle: [
            'font-bold',
            'text-typography-font-size-body-medium',
            'text-component-footer-category-label',
            'leading-typography-line-height-body-medium'
        ],
        logoContainer: [
            'lg:w-1/4',
            'flex',
            'flex-col',
            'gap-spacing-spacing-32'
        ],
        logoDescription: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-typography-font-weight-body',
            'leading-typography-line-height-body-medium',
            'text-component-footer-description-text'
        ],
        footerSectionWrapper: [
            'w-full',
            'py-spacing-spacing-40',
            'lg:py-spacing-spacing-32',
            'bg-component-footer-utility-bg'
        ],
        footerSocialLinksSection: [
            'm-auto',
            'flex',
            'flex-col',
            'lg:flex-row',
            'gap-spacing-spacing-48',
            'lg:gap-spacing-spacing-64',
            'justify-between',
            'max-w-screen-dimensions-max-width',
            'min-w-screen-dimensions-min-width',
            'px-general-spacing-margin-x'
        ],
        copyRightTextStyle: [
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'text-component-footer-legal-text',
            'lg:text-right',
            'lg:basis-1/2',
            'flex-1'
        ],
        legalLinkWrapper: [
            'flex',
            'gap-spacing-spacing-64',
            'lg:basis-1/2',
            'flex-1'
        ],
        legalLinkNavContainer: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-4',
            'basis-1/2',
            'flex-1'
        ],
        legalLinkListItem: [
            'p-spacing-spacing-2'
        ],
        legalLinkListLink: [
            'flex',
            'items-center',
            'text-component-footer-bottomBar-link-text',
            'hover:text-component-footer-bottomBar-link-text-hover',
            'focus:text-component-footer-bottomBar-link-text-focus'
        ],
        legalLinkListText: [
            'font-typography-font-weight-body-semibold',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'text-component-footer-bottomBar-link-text',
            'flex',
            'gap-spacing-spacing-8',
            'items-center'
        ]
    }
});
